import React, { useCallback, useEffect, useState } from "react";
import { http, inr, requestErrorMessage } from "@/lib/erp";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { MasterManager } from "@/components/MasterManager";
import { PriceLists } from "@/components/PriceLists";
import { Plus, Search, Package, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", code: "", barcode: "", composition: "", manufacturer: "", category: "General",
  hsn: "", gst_rate: 12, unit: "STRIP", pack_size: "1x10", mrp: 0, ptr: 0, pts: 0,
  purchase_price: 0, selling_price: 0, batch_no: "", mfg_date: "", expiry_date: "",
  stock: 0, reorder_level: 10, rack: "", warehouse: "Main Warehouse", group: "",
};

function expiryBadge(exp) {
  if (!exp) return null;
  let days = 999;
  try {
    let ed;
    if (exp.includes("/")) { const [mm, yy] = exp.split("/"); ed = new Date(2000 + +yy, +mm - 1, 1); }
    else { const p = exp.split("-"); ed = new Date(+p[0], +p[1] - 1, 1); }
    days = Math.round((ed - new Date()) / 86400000);
  } catch { }
  const tone = days < 0 ? "bg-rose-100 text-rose-700" : days < 90 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600";
  return <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${tone}`}>{exp}</span>;
}

export default function Products() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [cats, setCats] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [masterErrors, setMasterErrors] = useState({});

  const load = useCallback(async () => {
    try { const r = await http.get("/products", { params: { search } }); setRows(r.data); setLoadError(""); }
    catch (e) { setLoadError(requestErrorMessage(e, "load products")); }
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const loadCategories = useCallback(async () => {
    try { const r = await http.get("/masters/categories"); setCats(r.data); setMasterErrors((e) => ({ ...e, categories: "" })); }
    catch (e) { setMasterErrors((errors) => ({ ...errors, categories: requestErrorMessage(e, "load product categories") })); }
  }, []);
  const loadGroups = useCallback(async () => {
    try { const r = await http.get("/masters/product_groups"); setGroups(r.data); setMasterErrors((e) => ({ ...e, groups: "" })); }
    catch (e) { setMasterErrors((errors) => ({ ...errors, groups: requestErrorMessage(e, "load product groups") })); }
  }, []);
  useEffect(() => { loadCategories(); loadGroups(); }, [open, loadCategories, loadGroups]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setEditId(p.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Product name required");
    try {
      if (editId) await http.put(`/products/${editId}`, form);
      else await http.post("/products", form);
      toast.success(editId ? "Product updated" : "Product added");
      setOpen(false); load();
    } catch (e) { toast.error(requestErrorMessage(e, editId ? "update product" : "add product")); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete product?")) return;
    try { await http.delete(`/products/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(requestErrorMessage(e, "delete product")); }
  };

  const num = (k) => (e) => setForm({ ...form, [k]: +e.target.value });
  const txt = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Products</h1>
          <p className="text-sm text-muted-foreground">{rows.length} pharma SKUs · batch & expiry tracked</p>
        </div>
      </div>

      <Tabs defaultValue="products">
        <TabsList className="flex flex-wrap h-auto justify-start">
          <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="groups" data-testid="tab-groups">Groups</TabsTrigger>
          <TabsTrigger value="pricelists" data-testid="tab-pricelists">Price Lists</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-4 space-y-4">
          <div className="flex justify-end"><Button onClick={openNew} data-testid="add-product-button"><Plus className="h-4 w-4 mr-1" /> Add Product</Button></div>
      <Card className="p-0 overflow-hidden">
        {loadError && <div role="alert" className="flex items-center justify-between gap-3 border-b border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"><span>{loadError}</span><Button size="sm" variant="outline" onClick={load}>Retry</Button></div>}
        <div className="p-4 border-b">
          <div className="relative max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search name, composition…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="product-search" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-medium">Product</th>
                <th className="px-4 py-3 font-medium">HSN</th>
                <th className="px-4 py-3 font-medium">Batch</th>
                <th className="px-4 py-3 font-medium">Expiry</th>
                <th className="px-4 py-3 font-medium text-right">MRP</th>
                <th className="px-4 py-3 font-medium text-right">PTR</th>
                <th className="px-4 py-3 font-medium text-center">GST</th>
                <th className="px-4 py-3 font-medium text-right">Stock</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const low = Number(p.stock) <= Number(p.reorder_level);
                return (
                  <tr key={p.id} className="border-t hover:bg-slate-50" data-testid={`product-row-${p.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-muted-foreground">{p.composition} · {p.pack_size}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{p.hsn}</td>
                    <td className="px-4 py-3 font-mono text-xs">{p.batch_no || "—"}</td>
                    <td className="px-4 py-3">{expiryBadge(p.expiry_date) || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono">{inr(p.mrp)}</td>
                    <td className="px-4 py-3 text-right font-mono">{inr(p.ptr)}</td>
                    <td className="px-4 py-3 text-center">{p.gst_rate}%</td>
                    <td className={`px-4 py-3 text-right font-mono ${low ? "text-rose-600 font-semibold" : ""}`}>{p.stock}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(p)} className="p-1.5 hover:bg-slate-100 rounded" data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4 text-slate-600" /></button>
                        <button onClick={() => del(p.id)} className="p-1.5 hover:bg-rose-50 rounded" data-testid={`delete-product-${p.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loadError && rows.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-muted-foreground"><Package className="h-8 w-8 mx-auto mb-2 opacity-40" />No products</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
        </TabsContent>

        <TabsContent value="categories">
          <MasterManager kind="categories" title="Category" testid="category"
            fields={[{ key: "name", label: "Category Name", placeholder: "e.g. Antibiotic" }, { key: "description", label: "Description" }]} />
        </TabsContent>
        <TabsContent value="groups">
          <MasterManager kind="product_groups" title="Group" testid="group"
            fields={[{ key: "name", label: "Group Name", placeholder: "e.g. Oncology" }, { key: "description", label: "Description" }]} />
        </TabsContent>
        <TabsContent value="pricelists">
          <PriceLists />
        </TabsContent>
      </Tabs>

      {(masterErrors.categories || masterErrors.groups) && <div role="status" className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3 space-y-2">
        {masterErrors.categories && <div className="flex items-center justify-between gap-2"><span>{masterErrors.categories}</span><Button size="sm" variant="outline" onClick={loadCategories}>Retry</Button></div>}
        {masterErrors.groups && <div className="flex items-center justify-between gap-2"><span>{masterErrors.groups}</span><Button size="sm" variant="outline" onClick={loadGroups}>Retry</Button></div>}
      </div>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2"><Label>Name *</Label><Input value={form.name} onChange={txt("name")} className="mt-1.5" data-testid="product-name-input" /></div>
            <div><Label>Code / SKU</Label><Input value={form.code} onChange={txt("code")} className="mt-1.5 font-mono" /></div>
            <div><Label>Barcode</Label><Input value={form.barcode} onChange={txt("barcode")} placeholder="Scan or type EAN/UPC" className="mt-1.5 font-mono" data-testid="product-barcode-input" /></div>
            <div className="sm:col-span-2"><Label>Composition</Label><Input value={form.composition} onChange={txt("composition")} className="mt-1.5" /></div>
            <div><Label>Manufacturer</Label><Input value={form.manufacturer} onChange={txt("manufacturer")} className="mt-1.5" /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category || ""} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="product-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="General">General</SelectItem>
                  {cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Group</Label>
              <Select value={form.group || ""} onValueChange={(v) => setForm({ ...form, group: v })}>
                <SelectTrigger className="mt-1.5" data-testid="product-group-select"><SelectValue placeholder="Select group" /></SelectTrigger>
                <SelectContent>
                  {groups.length === 0 && !masterErrors.groups && <div className="px-2 py-1.5 text-sm text-muted-foreground">Add groups in the Groups tab</div>}
                  {groups.map((g) => <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>HSN Code</Label><Input value={form.hsn} onChange={txt("hsn")} className="mt-1.5 font-mono" data-testid="product-hsn-input" /></div>
            <div>
              <Label>GST Rate</Label>
              <Select value={String(form.gst_rate)} onValueChange={(v) => setForm({ ...form, gst_rate: +v })}>
                <SelectTrigger className="mt-1.5" data-testid="product-gst-select"><SelectValue /></SelectTrigger>
                <SelectContent>{[0, 5, 12, 18, 28].map((g) => <SelectItem key={g} value={String(g)}>{g}%</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Unit</Label><Input value={form.unit} onChange={txt("unit")} className="mt-1.5" /></div>
            <div><Label>Pack Size</Label><Input value={form.pack_size} onChange={txt("pack_size")} className="mt-1.5" /></div>
            <div><Label>MRP</Label><Input type="number" value={form.mrp} onChange={num("mrp")} className="mt-1.5" data-testid="product-mrp-input" /></div>
            <div><Label>PTR (Rate)</Label><Input type="number" value={form.ptr} onChange={num("ptr")} className="mt-1.5" data-testid="product-ptr-input" /></div>
            <div><Label>PTS</Label><Input type="number" value={form.pts} onChange={num("pts")} className="mt-1.5" /></div>
            <div><Label>Purchase Price</Label><Input type="number" value={form.purchase_price} onChange={num("purchase_price")} className="mt-1.5" /></div>
            <div><Label>Batch No</Label><Input value={form.batch_no} onChange={txt("batch_no")} className="mt-1.5 font-mono" data-testid="product-batch-input" /></div>
            <div><Label>Expiry (MM/YY)</Label><Input value={form.expiry_date} onChange={txt("expiry_date")} placeholder="12/28" className="mt-1.5 font-mono" data-testid="product-expiry-input" /></div>
            <div><Label>Stock Qty</Label><Input type="number" value={form.stock} onChange={num("stock")} className="mt-1.5" data-testid="product-stock-input" /></div>
            <div><Label>Reorder Level</Label><Input type="number" value={form.reorder_level} onChange={num("reorder_level")} className="mt-1.5" /></div>
            <div><Label>Rack / Location</Label><Input value={form.rack} onChange={txt("rack")} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-product-button">Save Product</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
